import React, { useState, useEffect } from 'react';
import { Heart, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from "@/lib/utils";
import gratitudeSound from '@/assets/gratitude-notification.mp3';
import { translations, type Locale } from '@/i18n/translations';
import { useAppContext } from "@/context/AppContext";

interface GratitudeBannerProps {
  message: string;
  isVisible: boolean;
  onClose: () => void;
}

const GratitudeBanner: React.FC<GratitudeBannerProps> = ({ message, isVisible, onClose }) => {
  // Initialize audio for the notification sound
  const [audio] = useState(() => (gratitudeSound ? new Audio(gratitudeSound) : null));
  const [hasPlayed, setHasPlayed] = useState<boolean>(false);
  const { language, t } = useAppContext();

  useEffect(() => {
    const stored = sessionStorage.getItem('gratitudeSoundPlayed');
    if (stored === 'true') {
      setHasPlayed(true);
    } else {
      sessionStorage.setItem('gratitudeSoundPlayed', 'false');
    }
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const alreadyPlayed = sessionStorage.getItem('gratitudeSoundPlayed') === 'true';
    if (alreadyPlayed) return;

    try {
      if (audio) {
        audio.volume = 0.5;
        audio.currentTime = 0;
        audio.play().catch(() => {});
      }
    } catch (error) {
      console.warn("Audio playback issue:", error);
    }

    sessionStorage.setItem('gratitudeSoundPlayed', 'true');

    const timer = setTimeout(() => onClose(), 8000);
    return () => clearTimeout(timer);
  }, [isVisible, audio, onClose]);
  
  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed top-2 left-0 right-0 z-50 mx-auto w-[94%] sm:w-auto sm:max-w-md sm:left-1/2 sm:-translate-x-1/2"
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <div className={cn(
            "bg-gradient-to-r from-white to-slate-100 dark:from-slate-800 dark:to-slate-900",
            "border border-primary/40 dark:border-primary/50 shadow-md rounded-lg overflow-hidden",
            "p-2.5 sm:p-4 flex items-center"
          )}>
            <div className="flex items-start sm:items-center gap-2 sm:gap-3 w-full">
              <div className="relative flex-shrink-0 mt-0.5 sm:mt-0">
                <Heart className="h-5 w-5 sm:h-7 sm:w-7 text-primary fill-primary" />
                <motion.div
                  className="absolute inset-0 rounded-full bg-primary/20"
                  animate={{ scale: [1, 1.5, 1], opacity: [0.7, 0, 0.7] }}
                  transition={{ repeat: Infinity, duration: 3 }}
                />
              </div>
              <div className="min-w-0 flex-1 pr-2">
                <h3 className="font-medium text-xs sm:text-sm text-navy dark:text-white truncate">
                    {t('gratitudeBanner.someOneSentYouGratitude', 'Someone sent you gratitude')}
                </h3>
                <p className="text-xs text-navy dark:text-gray-100 mt-0.5 italic font-medium line-clamp-2">
                  "{message}"
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex-shrink-0"
                aria-label="Close notification"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GratitudeBanner;
